/**
 * RouteErrorBoundary — last-resort UI for runtime errors inside a route.
 *
 * React doesn't yet have a hooks-based error boundary, so this is a class
 * component. It catches throws from descendants (including async errors
 * surfaced via `useQuery`'s `onError` → re-throw pattern) and shows a
 * friendly retry surface instead of a blank page.
 *
 * Why one per route, not one at the app root:
 *   * A failure on `/orders` shouldn't blank `/alerts`. Each protected
 *     route is wrapped independently in `App.tsx`.
 *   * The boundary doesn't auto-recover — a refresh / "Try again" click
 *     re-mounts the subtree by bumping a key. That's intentional: the
 *     page might have entered an inconsistent state and a clean re-mount
 *     is the safest fallback.
 *
 * Reporting:
 *   In production we log to console and (optionally) a centralised error
 *   tracker. Sentry / Bugsnag wiring is on §16; today we only log so the
 *   browser dev-tools stay useful and the hook is in place.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

import { AppLayout } from "@/components/AppLayout";
import { Card, PageHero } from "@/components/Card";
import { Button } from "@/components/ui/button";
import { reportError } from "@/sentry";

interface Props {
  /** Friendly route label, e.g. "Orders" — surfaces in the fallback copy. */
  routeName?: string;
  children: ReactNode;
}

interface State {
  /** Bumped each time we want to re-mount the subtree. */
  resetKey: number;
  /** The error itself (if any). */
  error: Error | null;
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { resetKey: 0, error: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, {
      source:    "route_error_boundary",
      routeName: this.props.routeName,
      extra:     { componentStack: info.componentStack },
    });
  }

  reset = (): void => {
    this.setState((s) => ({ resetKey: s.resetKey + 1, error: null }));
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <AppLayout>
          <div className="rf-stagger space-y-6">
            <PageHero
              eyebrow={this.props.routeName ?? "Something went wrong"}
              title={<>Hmm, that didn't work</>}
              subtitle="The page hit an unexpected error. Your data is safe — try reloading the surface, and if it keeps happening, the audit log under Settings will show the last action that ran."
            />
            <Card className="px-6 py-8">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-berry/10">
                  <AlertTriangle className="h-4 w-4 text-berry" aria-hidden="true" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-ink">
                    Error: {this.state.error.name || "RuntimeError"}
                  </div>
                  <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded-md border border-line bg-canvas-2/50 p-3 font-mono text-[12px] text-ink-2">
                    {this.state.error.message}
                  </pre>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button onClick={this.reset} className="gap-1.5">
                      <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                      Try again
                    </Button>
                    <Button variant="outline" onClick={() => window.location.reload()}>
                      Reload page
                    </Button>
                    <Button variant="ghost" onClick={() => window.history.back()}>
                      Go back
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </AppLayout>
      );
    }

    // Bumping the key forces a full re-mount of the children — same trick
    // React Router uses internally. Way safer than trying to "patch" the
    // broken tree.
    return <div key={this.state.resetKey}>{this.props.children}</div>;
  }
}
